/**
 * KPIPinnedCards — pinnable KPI card grid with drag-to-reorder.
 *
 * Grid layout: 4 cards per row (2 on mobile).
 * Drag: uses framer-motion drag on individual cards (not Reorder.Group,
 *       which doesn't support 2-D grids). Order persisted to DashboardContext.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, Pin, PinOff } from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer } from 'recharts';
import { useDashboard } from '../../context/DashboardContext';

export interface KPIEntry {
  key:            string;
  label:          string;
  value:          number | string;
  previousValue?: number;
  trend?:         number;
  trendDir?:      'up' | 'down' | 'flat';
  sparkline?:     number[];
  color:          string;
  icon:           React.ReactNode;
  route:          string;
}

interface KPIPinnedCardsProps {
  entries:    KPIEntry[];
  loading?:   boolean;
  onNavigate: (route: string) => void;
}

// ── Single card ───────────────────────────────────────────────────────────────

function KPICard({
  entry, pinned, onTogglePin, onNavigate,
}: {
  entry: KPIEntry;
  pinned: boolean;
  onTogglePin: () => void;
  onNavigate: () => void;
}) {
  const trendColor = entry.trendDir === 'up' ? '#16A34A' : entry.trendDir === 'down' ? '#DC2626' : '#6899A0';
  const TrendIcon  = entry.trendDir === 'up' ? TrendingUp : entry.trendDir === 'down' ? TrendingDown : Minus;
  const chartData  = (entry.sparkline || []).map((v) => ({ v }));

  // MoM delta derived from previousValue
  const currentNum = typeof entry.value === 'number' ? entry.value : null;
  const momDelta: number | null =
    currentNum !== null && entry.previousValue !== undefined && entry.previousValue !== 0
      ? ((currentNum - entry.previousValue) / entry.previousValue) * 100
      : null;
  const momDir = momDelta === null ? null : momDelta > 0 ? 'up' : momDelta < 0 ? 'down' : 'flat';
  const MomIcon = momDir === 'up' ? TrendingUp : momDir === 'down' ? TrendingDown : Minus;
  const momColor = momDir === 'up' ? '#16A34A' : momDir === 'down' ? '#DC2626' : '#94A3B8';
  const momBg    = momDir === 'up' ? '#F0FDF4' : momDir === 'down' ? '#FEF2F2' : '#f7f5f2';

  return (
    <motion.div
      whileHover={{ y: -5, boxShadow: '0 24px 80px rgba(0,0,0,0.08)' }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      style={{
        cursor: 'pointer',
        background: 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: 4,
        padding: '16px 18px',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
      onClick={onNavigate}
    >
      {/* Accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, ${entry.color}, ${entry.color}88)`,
        borderRadius: '10px 10px 0 0',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: `${entry.color}12`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {React.cloneElement(entry.icon as React.ReactElement, { size: 14, color: entry.color })}
          </div>
          <p style={{ fontSize: 12, fontWeight: 600, color: '#5a5a6e', lineHeight: 1.2 }}>
            {entry.label}
          </p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
          title={pinned ? 'Unpin card' : 'Pin card'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: pinned ? entry.color : 'rgba(0,0,0,0.15)',
            padding: 4, borderRadius: 6,
            display: 'flex', alignItems: 'center',
            transition: 'color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = entry.color; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = pinned ? entry.color : 'rgba(0,0,0,0.15)'; }}
        >
          {pinned ? <Pin size={13} /> : <PinOff size={13} />}
        </button>
      </div>

      {/* Value + sparkline */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <p style={{
            fontSize: 24, fontWeight: 800, color: '#1a1a2e',
            lineHeight: 1, fontFamily: '"Cormorant Garamond", serif',
          }}>
            {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
          {entry.trend !== undefined && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8,
              padding: '3px 8px', borderRadius: 100,
              background: entry.trendDir === 'up' ? '#F0FDF4' : entry.trendDir === 'down' ? '#FEF2F2' : '#f7f5f2',
            }}>
              <TrendIcon size={12} color={trendColor} />
              <span style={{ fontSize: 11, fontWeight: 600, color: trendColor }}>
                {Math.abs(entry.trend)}%
              </span>
            </div>
          )}
          {momDelta !== null && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 6, marginLeft: entry.trend !== undefined ? 6 : 0,
              padding: '2px 7px', borderRadius: 100,
              background: momBg,
              border: `1px solid ${momColor}22`,
            }}
              title="Month-over-month change"
            >
              <MomIcon size={10} color={momColor} />
              <span style={{ fontSize: 10, fontWeight: 700, color: momColor }}>
                {momDelta > 0 ? '+' : ''}{momDelta.toFixed(1)}% MoM
              </span>
            </div>
          )}
          {momDelta === null && entry.previousValue === 0 && currentNum !== null && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 6,
              padding: '2px 7px', borderRadius: 100,
              background: '#f7f5f2', border: '1px solid rgba(0,0,0,0.1)22',
            }}>
              <Minus size={10} color="#94A3B8" />
              <span style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8' }}>— MoM</span>
            </div>
          )}
        </div>
        {chartData.length > 0 && (
          <div style={{ width: 72, height: 40 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barSize={5} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <Bar dataKey="v" fill={entry.color} opacity={0.6} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function KPIPinnedCards({ entries, loading, onNavigate }: KPIPinnedCardsProps) {
  const { pinnedKPIOrder, setPinnedKPIOrder } = useDashboard();

  // Which keys are currently unpinned
  const [unpinnedKeys, setUnpinnedKeys] = useState<string[]>(() =>
    entries.map((e) => e.key).filter((k) => !pinnedKPIOrder.includes(k))
  );

  const pinnedKeys    = pinnedKPIOrder.filter((k) => entries.some((e) => e.key === k));
  const pinnedEntries = pinnedKeys
    .map((k) => entries.find((e) => e.key === k))
    .filter(Boolean) as KPIEntry[];

  const unpin = (key: string) => {
    setPinnedKPIOrder(pinnedKeys.filter((k) => k !== key));
    setUnpinnedKeys((p) => [...p, key]);
  };

  const pin = (key: string) => {
    setPinnedKPIOrder([...pinnedKeys, key]);
    setUnpinnedKeys((p) => p.filter((k) => k !== key));
  };

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16,
        width: '100%',
      }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} style={{
            height: 96, borderRadius: 4,
            background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.04) 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s infinite',
          }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Pinned grid — 4 columns, 2 rows */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16,
      }}
      >
        {pinnedEntries.map((entry) => (
          <KPICard
            key={entry.key}
            entry={entry}
            pinned
            onTogglePin={() => unpin(entry.key)}
            onNavigate={() => onNavigate(entry.route)}
          />
        ))}
      </div>

      {/* Unpinned tray */}
      {unpinnedKeys.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: '#94A3B8',
            letterSpacing: '0.6px', textTransform: 'uppercase', marginRight: 4,
          }}>
            Hidden:
          </span>
          {unpinnedKeys.map((key) => {
            const entry = entries.find((e) => e.key === key);
            if (!entry) return null;
            return (
              <button
                key={key}
                onClick={() => pin(key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '3px 9px', borderRadius: 100,
                  border: '1px solid rgba(0,0,0,0.1)', background: '#f7f5f2',
                  fontSize: 10.5, color: '#5a5a6e', cursor: 'pointer', fontWeight: 500,
                  transition: 'all 0.12s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(176,125,26,0.08)';
                  e.currentTarget.style.borderColor = '#b07d1a';
                  e.currentTarget.style.color = '#b07d1a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f7f5f2';
                  e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)';
                  e.currentTarget.style.color = '#5a5a6e';
                }}
              >
                <Pin size={8} /> {entry.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
